import { c as _c } from "react/compiler-runtime";
/**
 * React Query hooks for the levels below a campaign. The campaigns list asks
 * for a level only when its row is expanded.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdLevel, AdLevelMetrics, AdRoutingNode } from "@/types/ad-hierarchy";
import { fetchAdRouting, fetchAdSetAds, fetchCampaignAdSets, setLevelRouting, syncAdHierarchy } from "@/api/provider/opportunities/ad-hierarchy";
import { campaignPipelineAutomationKeys } from "@/api/provider/opportunities/campaign-pipeline-automation-query";
export const adHierarchyKeys = {
  all: ["ad-hierarchy"] as const,
  campaignAdSets: (campaignId: string) => [...adHierarchyKeys.all, "campaign-ad-sets", campaignId] as const,
  adSetAds: (adSetId: string) => [...adHierarchyKeys.all, "ad-set-ads", adSetId] as const,
  routing: (campaignId: string) => [...adHierarchyKeys.all, "routing", campaignId] as const
};
export function useCampaignAdSets(campaignId, enabled) {
  const $ = _c(8);
  let t0;
  if ($[0] !== campaignId) {
    t0 = adHierarchyKeys.campaignAdSets(campaignId);
    $[0] = campaignId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== campaignId) {
    t1 = () => fetchCampaignAdSets(campaignId);
    $[2] = campaignId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== enabled || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      enabled
    };
    $[4] = enabled;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
}
export function useAdSetAds(adSetId, enabled) {
  const $ = _c(8);
  let t0;
  if ($[0] !== adSetId) {
    t0 = adHierarchyKeys.adSetAds(adSetId);
    $[0] = adSetId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== adSetId) {
    t1 = () => fetchAdSetAds(adSetId);
    $[2] = adSetId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== enabled || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      enabled
    };
    $[4] = enabled;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
}
export function useAdRouting(campaignId) {
  const $ = _c(8);
  let t0;
  if ($[0] !== campaignId) {
    t0 = adHierarchyKeys.routing(campaignId);
    $[0] = campaignId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== campaignId) {
    t1 = () => fetchAdRouting(campaignId);
    $[2] = campaignId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(campaignId);
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
export function useSyncAdHierarchy() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: adHierarchyKeys.all
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp(campaignId) {
  return syncAdHierarchy(campaignId);
}
export function useSetLevelRouting(campaignId) {
  const $ = _c(3);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== campaignId || $[1] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: adHierarchyKeys.routing(campaignId)
        });
        queryClient.invalidateQueries({
          queryKey: campaignPipelineAutomationKeys.summaries(campaignId)
        });
      }
    };
    $[0] = campaignId;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useMutation(t0);
}
function _temp2(t0) {
  const {
    level,
    id,
    pipelineId
  } = t0;
  return setLevelRouting(level, id, pipelineId);
}